/**
 * Admin - Email Templates Page
 *
 * Manage email templates used for system notifications (invitations, welcome, etc.)
 * Templates are automatically wrapped in a branded layout with Mazo logo and footer.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../platform/api';
import {
  ArrowLeft,
  Mail,
  Save,
  Send,
  Code,
  Eye,
  EyeOff,
} from 'lucide-react';

interface EmailTemplate {
  slug: string;
  name: string;
  description: string;
  subject: string;
  html: string;
  variables: string[];
}

const TEMPLATE_META: Record<string, { name: string; description: string; variables: string[] }> = {
  invitation: {
    name: 'Invitation',
    description: 'Sent when an admin invites a new user to the platform.',
    variables: ['{{name}}', '{{email}}', '{{inviteUrl}}', '{{planName}}', '{{trialDays}}'],
  },
  welcome: {
    name: 'Welcome',
    description: 'Sent after a user completes sign-up and onboarding.',
    variables: ['{{name}}', '{{email}}', '{{loginUrl}}'],
  },
  'trial-expiring': {
    name: 'Trial Expiring',
    description: 'Sent when a user\'s trial is about to expire (3 days before).',
    variables: ['{{name}}', '{{email}}', '{{daysRemaining}}', '{{trialEndsAt}}', '{{upgradeUrl}}'],
  },
  'trial-expired': {
    name: 'Trial Expired',
    description: 'Sent when a user\'s trial has expired.',
    variables: ['{{name}}', '{{email}}', '{{upgradeUrl}}'],
  },
};

export function AdminEmailTemplatesPage() {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [testing, setTesting] = useState<Record<string, boolean>>({});
  const [previewing, setPreviewing] = useState<Record<string, boolean>>({});
  const [previewHtml, setPreviewHtml] = useState<Record<string, string>>({});
  const [previewLoading, setPreviewLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadTemplates();
  }, []);

  async function loadTemplates() {
    try {
      setLoading(true);
      setError(null);
      const result = await apiRequest<{ data: EmailTemplate[] }>('/api/admin/email-templates');
      const data = (result as any)?.data || result;
      if (Array.isArray(data)) {
        const enriched = data.map((t: EmailTemplate) => ({
          ...t,
          name: TEMPLATE_META[t.slug]?.name || t.slug,
          description: TEMPLATE_META[t.slug]?.description || '',
          variables: TEMPLATE_META[t.slug]?.variables || [],
        }));
        setTemplates(enriched);
      } else {
        setTemplates(
          Object.entries(TEMPLATE_META).map(([slug, meta]) => ({
            slug,
            name: meta.name,
            description: meta.description,
            subject: '',
            html: '',
            variables: meta.variables,
          }))
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }

  function updateTemplate(slug: string, field: 'subject' | 'html', value: string) {
    setTemplates(prev =>
      prev.map(t => (t.slug === slug ? { ...t, [field]: value } : t))
    );
    // Clear preview when content changes so it refreshes on next toggle
    if (field === 'html' && previewing[slug]) {
      loadPreview(slug, value);
    }
  }

  async function handleSave(slug: string) {
    const template = templates.find(t => t.slug === slug);
    if (!template) return;

    setSaving(prev => ({ ...prev, [slug]: true }));
    try {
      await apiRequest(`/api/admin/email-templates/${slug}`, {
        method: 'PATCH',
        body: { subject: template.subject, html: template.html },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setSaving(prev => ({ ...prev, [slug]: false }));
    }
  }

  async function handleTest(slug: string) {
    setTesting(prev => ({ ...prev, [slug]: true }));
    try {
      await apiRequest(`/api/admin/email-templates/${slug}/test`, {
        method: 'POST',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send test email');
    } finally {
      setTesting(prev => ({ ...prev, [slug]: false }));
    }
  }

  const loadPreview = useCallback(async (slug: string, html?: string) => {
    const template = templates.find(t => t.slug === slug);
    const content = html ?? template?.html;
    if (!content) {
      setPreviewHtml(prev => ({ ...prev, [slug]: '' }));
      return;
    }

    setPreviewLoading(prev => ({ ...prev, [slug]: true }));
    try {
      const result = await apiRequest<{ data: { html: string } }>('/api/admin/email-templates/preview', {
        method: 'POST',
        body: { html: content },
      });
      const branded = (result as any)?.data?.html || (result as any)?.html || '';
      setPreviewHtml(prev => ({ ...prev, [slug]: branded }));
    } catch {
      setPreviewHtml(prev => ({ ...prev, [slug]: '<p style="color:red;">Failed to load preview</p>' }));
    } finally {
      setPreviewLoading(prev => ({ ...prev, [slug]: false }));
    }
  }, [templates]);

  function togglePreview(slug: string) {
    const isNowPreviewing = !previewing[slug];
    setPreviewing(prev => ({ ...prev, [slug]: isNowPreviewing }));
    if (isNowPreviewing && !previewHtml[slug]) {
      loadPreview(slug);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Mail className="w-6 h-6" />
                Email Templates
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage email templates for system notifications. All emails are automatically wrapped in a branded layout with the Mazo logo and footer.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-mazo-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading templates...</p>
          </div>
        )}

        {!loading && (
          <div className="space-y-6">
            {templates.map(template => (
              <div
                key={template.slug}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                    <p className="mt-1 text-sm text-gray-500">{template.description}</p>
                  </div>
                </div>

                {/* Variables */}
                {template.variables.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Code className="w-4 h-4 text-gray-400" />
                      <span className="text-xs font-medium text-gray-500 uppercase">Available Variables</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {template.variables.map(v => (
                        <span
                          key={v}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-700"
                        >
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Subject */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={template.subject}
                    onChange={(e) => updateTemplate(template.slug, 'subject', e.target.value)}
                    placeholder="Email subject line..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent"
                  />
                </div>

                {/* HTML Body + Preview side by side when preview is open */}
                <div className={`mb-4 ${previewing[template.slug] ? 'grid grid-cols-2 gap-4' : ''}`}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      HTML Body <span className="font-normal text-gray-400">(content only — branded wrapper is added automatically)</span>
                    </label>
                    <textarea
                      value={template.html}
                      onChange={(e) => updateTemplate(template.slug, 'html', e.target.value)}
                      placeholder='<h2 style="color: #ffffff;">Hello {{firstName}},</h2>&#10;<p>Your email content here...</p>'
                      rows={previewing[template.slug] ? 16 : 8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent font-mono text-sm"
                    />
                  </div>

                  {/* Preview Panel */}
                  {previewing[template.slug] && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Preview
                      </label>
                      {previewLoading[template.slug] ? (
                        <div className="flex items-center justify-center h-[350px] bg-gray-900 rounded-lg border border-gray-300">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        </div>
                      ) : (
                        <EmailPreviewIframe html={previewHtml[template.slug] || ''} />
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                  <button
                    onClick={() => handleSave(template.slug)}
                    disabled={saving[template.slug]}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    <Save className="w-4 h-4" />
                    {saving[template.slug] ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => togglePreview(template.slug)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                      previewing[template.slug]
                        ? 'bg-mazo-50 text-mazo-700 border border-mazo-200 hover:bg-mazo-100'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {previewing[template.slug] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {previewing[template.slug] ? 'Hide Preview' : 'Preview'}
                  </button>
                  <button
                    onClick={() => handleTest(template.slug)}
                    disabled={testing[template.slug]}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    <Send className="w-4 h-4" />
                    {testing[template.slug] ? 'Sending...' : 'Send Test'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Renders branded email HTML inside a sandboxed iframe */
function EmailPreviewIframe({ html }: { html: string }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html || '<html><body style="background:#0a0a0a;color:rgba(255,255,255,0.4);font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;"><p>Enter HTML content and click Preview</p></body></html>');
    doc.close();
  }, [html]);

  return (
    <iframe
      ref={iframeRef}
      title="Email Preview"
      sandbox=""
      className="w-full h-[350px] rounded-lg border border-gray-300 bg-gray-900"
      style={{ border: 'none', borderRadius: '8px', outline: '1px solid #d1d5db' }}
    />
  );
}
